import {
    Button, Divider, Icon, Stack, Box,
    Typography, FormControlLabel, Checkbox
} from '@mui/material'

import { Fragment, useEffect, useRef, useState } from 'react'

import { useSelector } from 'react-redux'

import { useLandingZoneMigration } from '@features/hooks/LandingZones/migration'

import Link from '@components/Link'
import Alert from '@components/Alert'
import Datatable from '@components/Datatable'

import RequestHelp from '@views/Landingzones/Migration/Components/RequestHelp'

export const Main = () => {
    const dataStore = useSelector(state => state.landingZoneMigration)

    const { getStatus, handleNext, handlePrevious, handleRefreshMainLogs } = useLandingZoneMigration()

    const [inProgress, setInProgress] = useState(false)
    const [completed, setCompleted] = useState(false)
    const [checked, setChecked] = useState(false)
    const [noProblem, setNoProblem] = useState(true)
    const [noNsgProblem, setNoNsgProblem] = useState(true)

    const timer = useRef()

    const headers = useRef({
        nsg: [
            {field: 'nsgName', headerName: 'Name', flex: 1, sortable: false},
            {field: 'nsgRuleName', headerName: 'Rule', flex: 1, sortable: false},
            {field: 'flowDirection', headerName: 'Direction', flex: 1, sortable: false},
            {field: 'protocol', headerName: 'Protocol', flex: 1, sortable: false},
            {field: 'source', headerName: 'Source', flex: 1, sortable: false},
            {field: 'destination', headerName: 'Destination', flex: 1, sortable: false},
            {field: 'port', headerName: 'Port', flex: 1, sortable: false}
        ],
        app: [
            {field: 'name', headerName: 'Name', flex: 1, sortable: false},
            {field: 'privateIpAddress', headerName: 'Private IP', flex: 1, sortable: false},
        ]
    })

    useEffect(() => {
        timer.current = setInterval(checkInterval, 10000)
        return () => { clearInterval(timer.current) }
    }, [])

    useEffect(() => {
        setInProgress(dataStore.selected.inProgress)
        setCompleted(dataStore.selected.isCompleted)

        if (dataStore.selected.logs.app.length > 0 || dataStore.selected.logs.nsg.length > 0)
            setNoProblem(false)

        if (dataStore.selected.logs.nsg.length > 0)
            setNoNsgProblem(false)
    }, [dataStore.selected])

    const checkInterval = () => {
        getStatus(dataStore.selected.lzId, false)
    }

    return (
        <Fragment>
            <Divider sx={{mt: 2}}/>
            <Box sx={{p: 2}}>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Typography variant={'h5'} sx={{mb: 1}}>Main Migration</Typography>
                    {!noProblem && (<Button variant={'tertiary'} startIcon={<Icon>refresh</Icon>} onClick={handleRefreshMainLogs} disabled={inProgress}>Refresh</Button>)}
                </Stack>
                {(!inProgress && !completed) && (
                    <Fragment>
                        
                        <Typography variant={'body1'} sx={{mb: 2, lineHeight: 1.8}}>
                            The outbound traffic has been migrated successfully! To make sure everything went correctly, you can check the state of the following resources:
                        </Typography>

                        <Box component="ul" sx={{mb: 2, pl: 3}}>
                            <li>
                                <Typography variant={'body2'} sx={{mb: 1}}>
                                    <strong>Landing zone default route table(s)</strong> <code>udr-iasp-&lt;region&gt;-lz-network</code>: you should see that all the routes are now routing towards a new IP which is the vWAN firewall.
                                </Typography>
                            </li>
                        </Box>

                        <Typography variant={'body1'} sx={{mb: 2, lineHeight: 1.8}}>
                            If it's correct, you can now perform the Main migration. To transition on-premises traffic routing from peering hub to vWAN hub, this step disables remote gateway usage on the hub virtual network peering, and disables route propagation on route tables.
                        </Typography>

                        {noProblem && (<Alert severity={'info'} title={'No problem detected.'} sx={{mb: 2}} />)}
                        {!noProblem && (
                            <Fragment>
                                <Typography variant={'body2'} sx={{mb: 2}}>Please fix <strong>{parseInt(dataStore.selected.logs.nsg.length) + parseInt(dataStore.selected.logs.app.length)}</strong> element(s) of the list below before migrate</Typography>
                                {dataStore.selected.logs.nsg.length > 0 && (
                                    <Box sx={{mb: 2}}>
                                        <Alert severity={'error'} title={'Flows not correctly managed'} icon={'error'} sx={{mb: 2}} />
                                        <Typography variant={'label'} sx={{mt: 2, mb: 2}}>Element to fix</Typography>
                                        <Datatable
                                            sxRoot={{mt: 2}}
                                            sxGrid={{height: '200px'}}
                                            disableColumnMenu={true}
                                            disableColumnSorting={true}
                                            headers={headers.current.nsg}
                                            rows={dataStore.selected?.logs?.nsg ?? []}
                                            getRowId={(row) => row.id}
                                        />
                                    </Box>
                                )}
                                {dataStore.selected.logs.app.length > 0 && (
                                    <Box sx={{mb: 2}}>
                                        <Alert severity={'error'} title={'App gateway called by MAIN detected'} icon={'error'} sx={{mb: 2}} />
                                        <Typography variant={'label'} sx={{mt: 2, mb: 2}}>Element to fix</Typography>
                                        <Datatable
                                            sxRoot={{mt: 2}}
                                            sxGrid={{height: '200px'}}
                                            disableColumnMenu={true}
                                            disableColumnSorting={true}
                                            headers={headers.current.app}
                                            rows={dataStore.selected?.logs?.app ?? []}
                                            getRowId={(row) => row.id}
                                        />
                                    </Box>
                                )}
                            </Fragment>
                        )}

                        <FormControlLabel
                            control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
                            label={<Stack direction={'row'} alignItems={'center'}><span>I confirm my application is</span>&nbsp;<Link external target={'_blank'} href={'https://digitalplatforms.totalenergies.com/documentation/platforms/azure-platform/inoxscale-platform/user-communication/major-network-change#required-actions-to-prepare-migration'}>ready to migrate</Link></Stack>}
                        />

                        <Alert sx={{mt: 2}} severity={'warning'} title={'Prepare you to a short interruption of service.'} />
                    </Fragment>
                )}

                {inProgress && (<Alert severity={'info'} title={'Main migration is in progress'} />)}
                {completed && (<Alert severity={'success'} title={'Main migration is done'} />)}

                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{mt: 2}}>
                    <RequestHelp />
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'end'} spacing={1}>
                        <Button variant={'secondary'} startIcon={<Icon>undo</Icon>} disabled={inProgress} onClick={handlePrevious}>Rollback</Button>
                        {!completed && (<Button startIcon={<Icon>sync_alt</Icon>} onClick={handleNext} disabled={(!checked || inProgress || !noNsgProblem)}>Migrate</Button>)}
                    </Stack>
                </Stack>
            </Box>
        </Fragment>
    )
}

export default Main